import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { TextBody, TextHeadline, TextTitle1, TextTitle3 } from '@cbhq/cds-web/typography';
import { ProgressBar } from '@cbhq/cds-web/visualizations/ProgressBar';
import { ProgressBarWithFixedLabels } from '@cbhq/cds-web/visualizations/ProgressBarWithFixedLabels';

import { AnalyticEvent } from ':fat/analytics/events';
import { logRender } from ':fat/analytics/logActions';
import { logFundsAccessCriticalStep, logFundsAccessError } from ':fat/analytics/logMetrics';
import { getRates } from ':fat/api/getRates';
import { BalancesBanner } from ':fat/components/BalancesBanner/BalancesBanner';
import { FundSettlementFAQ } from ':fat/components/FAQ/FundsSettlementFAQ';
import { TwoColumnLayout } from ':fat/components/PageLayout/TwoColumnLayout';
import { ALL_NETWORKS, CURRENCY_DATA } from ':fat/core/networks';
import { formatCrypto, formatCryptoTruncated, formatFiat } from ':fat/helpers/formatting';
import { niceDuration } from ':fat/helpers/time';
import { useFiatBalances } from ':fat/hooks/useBalances';
import { clearAllData } from ':fat/messaging/entities/databaseWorker/actions';
import { messages } from ':fat/pages/Scanning/ScanningPageMessages';
import { AppRoutes } from ':fat/routing/routes';
import { ScanningProgress } from ':fat/workers/blockchain/types';

export function ScanningPage() {
  const { formatMessage } = useIntl();
  const { fingerprint } = useParams();
  const navigate = useNavigate();
  const { isPhone } = useBreakpoints();

  useEffect(() => {
    logRender(AnalyticEvent.GetStartedPageRendered);
  }, []);

  useStartScanningWhenReady(fingerprint);
  const progress = usePollScanningProgress(1000);

  useEffect(() => {
    if (progress?.error) {
      logFundsAccessError('error_component');
    }
    if (progress?.done) {
      logFundsAccessCriticalStep({ step: 'loaded_balances' });
    }
  }, [progress]);

  const { data: rates } = useQuery({ queryKey: ['rates'], queryFn: getRates, staleTime: 60000 });
  const fiatValues = useFiatBalances(progress?.balances ?? {});
  const totalFiatValue = Object.values(fiatValues).reduce((a, b) => a + b, 0);

  const now = new Date();
  const totalAddresses = Object.values(progress?.totalAddresses ?? {}).reduce((a, b) => a + b, 0);
  const totalScanned = Object.values(progress?.addressesScanned ?? {}).reduce((a, b) => a + b, 0);
  const timeRemaining = Math.max(
    ...Object.values(progress?.estimatedCompletion ?? {}).map(
      (completion) => Number(completion) - Number(now),
    ),
  );

  const allAssetsHaveBalances =
    Object.keys(progress?.balances ?? {}).length === ALL_NETWORKS.length;

  const allBalancesAreEmpty =
    progress?.done && !Object.values(progress?.balances ?? {}).some((value) => value > 0n);

  const handleCancel = useCallback(() => {
    void clearAllData().then(() => {
      navigate(AppRoutes.SECURITY_FRAME_LOADER);
    });
  }, [navigate]);

  const handleWithdrawClick = useCallback(() => {
    if (fingerprint) {
      // If any addresses were entered from a previous run, clear them to avoid
      // unintentionally sending funds to the wrong place
      for (const network of ALL_NETWORKS) {
        localStorage.removeItem(`${network}-address`);
      }
      navigate(AppRoutes.ENTER_WALLET_ADDRESSES.replace(':fingerprint', fingerprint));
    }
  }, [navigate, fingerprint]);

  return (
    <TwoColumnLayout
      primaryContent={
        <VStack gap={3} width="100%">
          <VStack gap={2}>
            <TextTitle1 as="h1">{formatMessage(messages.scanningTitle)}</TextTitle1>
            <TextBody color="foregroundMuted" as="p">
              {formatMessage(messages.scanningDescription)}
            </TextBody>
          </VStack>
          <BalancesBanner />
          <VStack>
            <TextTitle3 as="p" spacingBottom={1}>
              {progress?.done
                ? formatMessage(messages.scanningComplete)
                : formatMessage(messages.scanningAvailableBalances)}
            </TextTitle3>
            {progress?.done ? (
              <HStack alignItems="center" gap={1}>
                <Icon name="circleCheckmark" size="s" color="positive" />
                <TextBody as="p" color="foregroundMuted">
                  {formatMessage(messages.scanningCompleteMessage)}
                </TextBody>
              </HStack>
            ) : (
              <ProgressBarWithFixedLabels labelPlacement="below">
                <ProgressBar progress={totalAddresses === 0 ? 0 : totalScanned / totalAddresses} />
              </ProgressBarWithFixedLabels>
            )}
            {progress?.error ? (
              <TextBody as="p" color="negative" spacingBottom={1}>
                {formatMessage(messages.scanningError)}
              </TextBody>
            ) : (
              <TextBody as="p" color="foregroundMuted" spacingBottom={1}>
                {!progress?.done && timeRemaining > 0
                  ? formatMessage(messages.timeRemaining, { time: niceDuration(timeRemaining) })
                  : ' '}
              </TextBody>
            )}
          </VStack>
          <VStack gap={1} width="100%" bordered borderRadius="rounded">
            <HStack
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              borderedBottom={!allBalancesAreEmpty}
            >
              <TextBody as="p">{formatMessage(messages.availableBalance)}</TextBody>
              <TextTitle3 as="p">{formatFiat(totalFiatValue)}</TextTitle3>
            </HStack>
            {!allBalancesAreEmpty && (
              <VStack spacingStart={2}>
                {ALL_NETWORKS.map((network) =>
                  (progress?.percentages[network] ?? 1) >= 1 &&
                  (progress?.balances[network] ?? 0n) === 0n ? null : (
                    <HStack
                      alignItems="center"
                      justifyContent="center"
                      key={network}
                      gap={1}
                      spacingVertical={1}
                      spacingEnd={2}
                    >
                      <Avatar alt="" src={CURRENCY_DATA[network].icon} size="xl" />
                      <VStack flexGrow={1}>
                        <TextHeadline as="p">{CURRENCY_DATA[network].name}</TextHeadline>
                      </VStack>
                      <VStack alignItems="flex-end">
                        {rates && (
                          <TextHeadline as="p">{formatFiat(fiatValues[network] ?? 0)}</TextHeadline>
                        )}
                        <TextBody color="foregroundMuted" as="p">
                          {isPhone
                            ? formatCryptoTruncated(network, progress?.balances[network] ?? 0n)
                            : formatCrypto(network, progress?.balances[network] ?? 0n)}
                        </TextBody>
                      </VStack>
                    </HStack>
                  ),
                )}
              </VStack>
            )}
          </VStack>
          {!allAssetsHaveBalances && (
            <VStack width="100%" bordered borderRadius="rounded">
              <HStack spacing={2} alignItems="center" gap={1} borderedBottom>
                <TextBody as="p">{formatMessage(messages.assestsWithNoBalances)}</TextBody>
                <Tooltip content={formatMessage(messages.noFundsDetectedTooltip)}>
                  <Icon name="info" size="s" color="foregroundMuted" />
                </Tooltip>
              </HStack>
              <VStack spacingStart={2}>
                {ALL_NETWORKS.map((network) =>
                  progress?.percentages[network] &&
                  ((progress?.percentages[network] ?? 1) < 1 ||
                    (progress?.balances[network] ?? 0n) > 0n) ? null : (
                    <HStack
                      alignItems="center"
                      justifyContent="center"
                      key={network}
                      gap={1}
                      spacingVertical={1}
                      spacingEnd={2}
                    >
                      <Avatar alt="" src={CURRENCY_DATA[network].icon} size="xl" />
                      <VStack flexGrow={1}>
                        <TextHeadline as="p">{CURRENCY_DATA[network].name}</TextHeadline>
                      </VStack>
                      <VStack alignItems="flex-end">
                        {rates && <TextHeadline as="p">{formatFiat(0)}</TextHeadline>}
                        <TextBody color="foregroundMuted" as="p">
                          {isPhone
                            ? formatCryptoTruncated(network, progress?.balances[network] ?? 0n)
                            : formatCrypto(network, progress?.balances[network] ?? 0n)}
                        </TextBody>
                      </VStack>
                    </HStack>
                  ),
                )}
              </VStack>
            </VStack>
          )}

          <HStack
            justifyContent="flex-end"
            spacing={1}
            gap={1}
            flexWrap={isPhone ? 'wrap-reverse' : 'nowrap'}
          >
            <Button variant="secondary" onClick={handleCancel} block={isPhone}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={allBalancesAreEmpty}
              onClick={handleWithdrawClick}
              numberOfLines={1}
              block={isPhone}
            >
              Withdraw all available balance
            </Button>
          </HStack>
        </VStack>
      }
      secondaryContent={<FundSettlementFAQ />}
    />
  );
}

function useStartScanningWhenReady(fingerprint?: string) {
  useEffect(() => {
    const poller = setInterval(poll, 100);

    function poll() {
      if (self.SANITY_CHECK_COMPLETE && typeof fingerprint === 'string') {
        void MessageBus.send('blockchainWorker', {
          kind: 'START_SCANNING',
          fingerprint: parseInt(fingerprint),
        });
        clearInterval(poller);
      }
    }

    return () => {
      clearInterval(poller);
    };
  }, [fingerprint]);
}

export function usePollScanningProgress(everyMS: number) {
  const [progress, setProgress] = useState<ScanningProgress | undefined>();
  useEffect(() => {
    const poller = setInterval(() => {
      void poll();
    }, everyMS);

    async function poll() {
      if (self.SANITY_CHECK_COMPLETE) {
        const { progress: newProgress } = await MessageBus.send(
          'blockchainWorker',
          { kind: 'GET_SCANNING_PROGRESS' },
          { expectReply: 'SCANNING_PROGRESS' },
        );
        if (newProgress) {
          setProgress(newProgress);
          if (newProgress.done) {
            clearInterval(poller);
          }
        }
      }
    }

    return () => {
      clearInterval(poller);
    };
  }, [everyMS]);

  return progress;
}
